import React from "react";
import { Link, useLocation } from "react-router-dom";

import { Tag } from "../Tag/Tag";
import { H1, Paragraph, Bold } from "../Typography/Typography";
import { FollowActions } from "../FollowActions/FollowActions";
import { TodaysAlbum } from "../TodaysAlbum/TodaysAlbum";
import { ClerkUser } from "../../api/clerk";

import "./ProfileHeader.scss";

type ProfileHeaderProps = {
  userData: ClerkUser;
  followersCount: number;
  albumsSharedCount: number;
  albumsListenedCount: number;
  isCurrentUser: boolean;
  isLoaded: boolean;
  showHistoryTab?: boolean;
};

function getFullName(userData: ClerkUser): string {
  if (userData.firstName || userData.lastName) {
    return `${userData.firstName ?? ""} ${userData.lastName ?? ""}`.trim();
  }

  return userData.username ?? "";
}

export function ProfileHeader({
  userData,
  followersCount,
  albumsSharedCount,
  albumsListenedCount,
  isCurrentUser,
  isLoaded,
  showHistoryTab = false,
}: ProfileHeaderProps) {
  const location = useLocation();
  const activeTab = location.pathname.endsWith("/friends")
    ? "friends"
    : location.pathname.endsWith("/history")
      ? "history"
      : location.pathname.endsWith("/listened")
        ? "listened"
        : "shared";
  const fullName = getFullName(userData);
  const friendsLabel = followersCount === 1 ? "friend" : "friends";
  const sharedLabel = albumsSharedCount === 1 ? "album shared" : "albums shared";
  const listenedLabel = albumsListenedCount === 1 ? "album listened" : "albums listened";

  return (
    <header className="profile-header">
      <div className="profile-header__top">
        <div className="profile-header__identity">
          <div className="profile-header__image">
            {Boolean(userData.imageUrl) && (
              <img src={userData.imageUrl} alt={fullName} width="120" height="120" />
            )}
          </div>
          <div>
            <H1 className="profile-header__username">
              <span>{fullName || "…"}</span>
              <Tag>@{userData.username}</Tag>
            </H1>
            <div className="profile-header__stats">
              <Paragraph>
                <Bold>{albumsListenedCount}</Bold> {listenedLabel}
              </Paragraph>
              <Paragraph>
                <Bold>{albumsSharedCount}</Bold> {sharedLabel}
              </Paragraph>
              <Paragraph>
                {activeTab === "friends" ? (
                  <>
                    <Bold>{followersCount}</Bold> {friendsLabel}
                  </>
                ) : (
                  <Link className="profile-header__friends-link" to={`/${userData.username}/friends`}>
                    <Bold>{followersCount}</Bold> {friendsLabel}
                  </Link>
                )}
              </Paragraph>
            </div>

            {isLoaded && !isCurrentUser && (
              <FollowActions otherUserId={userData.externalId} />
            )}
          </div>
        </div>

        <TodaysAlbum />
      </div>

      <nav className="profile-tabs" aria-label="Profile sections">
        <Link
          className={`profile-tabs__tab${activeTab === "listened" ? " profile-tabs__tab--active" : ""}`}
          to={`/${userData.username}/listened`}
          aria-current={activeTab === "listened" ? "page" : undefined}
        >
          Listened
        </Link>
        <Link
          className={`profile-tabs__tab${activeTab === "shared" ? " profile-tabs__tab--active" : ""}`}
          to={`/${userData.username}/shared`}
          aria-current={activeTab === "shared" ? "page" : undefined}
        >
          Shared
        </Link>
        {showHistoryTab && (
          <Link
            className={`profile-tabs__tab${activeTab === "history" ? " profile-tabs__tab--active" : ""}`}
            to={`/${userData.username}/history`}
            aria-current={activeTab === "history" ? "page" : undefined}
          >
            Your history
          </Link>
        )}
        <Link
          className={`profile-tabs__tab${activeTab === "friends" ? " profile-tabs__tab--active" : ""}`}
          to={`/${userData.username}/friends`}
          aria-current={activeTab === "friends" ? "page" : undefined}
        >
          Friends
        </Link>
      </nav>
    </header>
  );
}

export { getFullName };
